import { Fragment, useRef, useState } from 'react';

import { Typography } from '@scaleaq/scaleui';
import { Combobox } from '@scaleaq/sui-react/combobox';
import { FieldPrefab } from '@scaleaq/sui-react/field';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Z_INDEX } from '@/constants';
import { Icon } from '@/utils';

import { Loader } from '../Loader.tsx';
import {
  applyValueChange,
  buildComboboxOptions,
  getOptionLabel,
  matchesOption,
  optionKey,
  resolveSelectedValues,
  valuesEqual,
  valuesEqualList,
} from './controlledCreatableMultiSelect/controlledCreatableMultiSelect.functions.ts';
import {
  StyledChevron,
  StyledChip,
  StyledChipClear,
  StyledChips,
  StyledClear,
  StyledDescription,
  StyledField,
  StyledGroupLabel,
  StyledLabel,
  StyledPopup,
  StyledQueryInput,
  StyledValue,
} from './controlledCreatableMultiSelect/controlledCreatableMultiSelect.styles.ts';
import type {
  CreatableSelectOption,
  FlatOption,
  OptionSection,
} from './controlledCreatableMultiSelect/controlledCreatableMultiSelect.types.ts';
import { FieldWrapper } from './inputFields.style.ts';

export type { CreatableSelectOption };

type ControlledCreatableMultiSelectProps<T extends FieldValues, V> = {
  /** When false, the field only selects existing options. */
  allowCreate?: boolean;
  caution?: boolean | string;
  control: Control<T>;
  /** Created entries shown as selected, and kept out of the form field. */
  createdValues?: V[];
  critical?: boolean | string;
  /** Short help text shown between the label and the field. */
  description?: string;
  disabled?: boolean;
  getCreatedValue?: (input: string) => V;
  isLoading?: boolean;
  label: string;
  maxWidth?: string;
  name: Path<T>;
  onCreate?: (value: V) => void;
  /** Called with the full created-entry list when that list changes. */
  onCreatedValuesChange?: (values: V[]) => void;
  options: Array<CreatableSelectOption<V>>;
  placeholder?: string;
  render?: boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  zIndex?: number;
};

export const ControlledCreatableMultiSelect = <T extends FieldValues, V>({
  allowCreate = true,
  caution,
  control,
  createdValues = [],
  critical,
  description,
  disabled = false,
  getCreatedValue,
  isLoading = false,
  label,
  maxWidth,
  name,
  onCreate,
  onCreatedValuesChange,
  options,
  placeholder,
  render = true,
  rules,
  zIndex = Z_INDEX.navigation.menu,
}: ControlledCreatableMultiSelectProps<T, V>) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [createdOptions, setCreatedOptions] = useState<FlatOption<V>[]>([]);
  const selectedValuesRef = useRef<V[]>([]);
  const fieldDisabled = disabled || isLoading;
  const resolvedPlaceholder = placeholder ?? t('typeToSearchOrCreate');

  if (!render) return null;

  const { createOption, existingOptionKeys, items, knownOptions, sections } = buildComboboxOptions({
    allowCreate,
    createdOptions,
    createdValues,
    getCreatedValue,
    options,
    query,
  });
  const validationMessage = getValidationMessage({ caution, critical });
  const fieldColor = getFieldColor({ caution, critical });

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const nextSelected = resolveSelectedValues({
          createdValues,
          fieldValue: field.value,
          separateCreatedValues: Boolean(onCreatedValuesChange),
        });
        // Reuse the previous array when the selection is unchanged so the combobox keeps its query.
        const selected = valuesEqualList({ left: selectedValuesRef.current, right: nextSelected })
          ? selectedValuesRef.current
          : nextSelected;
        selectedValuesRef.current = selected;

        return (
          <FieldWrapper $maxWidth={maxWidth}>
            <FieldPrefab error={validationMessage}>
              <Combobox.Multiple.Root
                disabled={fieldDisabled}
                filter={(item, typed) =>
                  matchesOption({
                    createValue: createOption?.value,
                    item,
                    options: knownOptions,
                    typed,
                  })
                }
                isItemEqualToValue={(left, right) => valuesEqual({ left, right })}
                itemToStringLabel={item => getOptionLabel({ item, options: knownOptions })}
                items={items}
                onInputValueChange={setQuery}
                onValueChange={nextValue =>
                  applyValueChange({
                    catalogReady: !isLoading,
                    createOption,
                    createdValues,
                    currentValue: field.value,
                    existingOptionKeys,
                    knownOptions,
                    nextValue,
                    onChange: field.onChange,
                    onCreate,
                    onCreatedValuesChange,
                    setCreatedOptions,
                  })
                }
                value={selected}
              >
                <StyledLabel>
                  <Typography type={'label_medium'} weight={500}>
                    {label}
                  </Typography>
                </StyledLabel>
                {description && (
                  <StyledDescription secondary type={'body_small'}>
                    {description}
                  </StyledDescription>
                )}
                <StyledField $color={fieldColor}>
                  <StyledChips>
                    <StyledValue>
                      {() => (
                        <>
                          {selected.map(item => (
                            <StyledChip key={optionKey(item)}>
                              {getOptionLabel({ item, options: knownOptions })}
                              <StyledChipClear aria-label={t('remove')} type='button'>
                                <Icon.X size={'small'} />
                              </StyledChipClear>
                            </StyledChip>
                          ))}
                          <StyledQueryInput
                            placeholder={selected.length === 0 ? resolvedPlaceholder : undefined}
                          />
                        </>
                      )}
                    </StyledValue>
                  </StyledChips>
                  <StyledClear aria-label={t('removeAll')} type='button' />
                  <StyledChevron type='button'>
                    <Combobox.Multiple.Icon useChevron />
                  </StyledChevron>
                </StyledField>
                <StyledPopup positionerProps={{ style: { zIndex } }}>
                  <Loader render={isLoading} />
                  <Combobox.Multiple.Empty>{t('noItemsFound')}</Combobox.Multiple.Empty>
                  <Combobox.Multiple.List>
                    {renderOptionSections({ sections })}
                    {createOption ? (
                      <Combobox.Multiple.Option
                        key={optionKey(createOption.value)}
                        value={createOption.value}
                      >
                        {t('createOption', { name: createOption.label })}
                      </Combobox.Multiple.Option>
                    ) : null}
                  </Combobox.Multiple.List>
                </StyledPopup>
              </Combobox.Multiple.Root>
            </FieldPrefab>
          </FieldWrapper>
        );
      }}
    />
  );
};

const getValidationMessage = ({
  caution,
  critical,
}: {
  caution?: boolean | string;
  critical?: boolean | string;
}) => {
  if (typeof critical === 'string') return critical;
  if (typeof caution === 'string') return caution;
  return undefined;
};

const getFieldColor = ({
  caution,
  critical,
}: {
  caution?: boolean | string;
  critical?: boolean | string;
}): 'caution' | 'critical' | 'neutral' => {
  if (critical) return 'critical';
  if (caution) return 'caution';
  return 'neutral';
};

const renderOptionSections = <V,>({ sections }: { sections: OptionSection<V>[] }) =>
  sections.map((section, index) => {
    const optionNodes = section.options.map(option => (
      <Combobox.Multiple.Option
        disabled={option.disabled}
        key={optionKey(option.value)}
        value={option.value}
      >
        {option.label}
      </Combobox.Multiple.Option>
    ));

    if (!section.label) {
      return <Fragment key={`options-${index}`}>{optionNodes}</Fragment>;
    }

    return (
      <Combobox.Multiple.Group key={`${section.label}-${index}`}>
        <StyledGroupLabel>{section.label}</StyledGroupLabel>
        {optionNodes}
      </Combobox.Multiple.Group>
    );
  });
